// Home page: welcomes visitors with an enhanced hero section, featured products,
// and an improved about section introducing Justin & Dave.
// Styles imported from the dedicated Home.css file.

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import '../styles/Home.css';
import '../styles/Store.css'; // for cart styles
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import "react-multi-carousel/lib/styles.css";
import Carousel from "react-multi-carousel";

// Import the images with fallback paths for production
import daveImage from '../assets/DaveAbbott.png';
import justinImage from '../assets/justin_cropped_taller.png';

// Fallback paths for production build - use process.env.PUBLIC_URL for proper deployment
const daveImageFallback = `${process.env.PUBLIC_URL}/assets/DaveAbbott.png`;
const justinImageFallback = `${process.env.PUBLIC_URL}/assets/justin_cropped_taller.png`;

// Enhanced featured product interface
interface FeaturedProduct {
  id: string;
  name: string;
  imageUrl: string;
  price: number;
  rating: number;
  category: string;
  badge?: string;
  featured?: boolean;
  stock?: number;
  description?: string;
}

interface CartItem {
  product: FeaturedProduct;
  quantity: number;
}

const Home: React.FC = () => {
  // State for animation triggers
  const [isVisible, setIsVisible] = useState<{[key: string]: boolean}>({
    about: false,
    products: false,
    newsletter: false
  });

  const [selectedProduct, setSelectedProduct] = useState<FeaturedProduct | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProduct[]>([]);
  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const [cartOpen, setCartOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Intersection Observer for animation triggering
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setIsVisible(prev => ({ ...prev, [sectionId]: true }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // Observe sections for animation
    const sections = ['about', 'featured-products', 'newsletter'];
    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  // Fetch featured products from Firestore
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('featured', '==', true));
        const snapshot = await getDocs(q);
        const products: FeaturedProduct[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FeaturedProduct[];
        setFeaturedProducts(products);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      }
    };
    fetchFeaturedProducts();
  }, []);

  // Format price for display
  const formatPrice = (price: number): string => {
    return price.toFixed(2);
  };

  // Handle newsletter submission
  const handleNewsletterSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // In a real application, this would send the email to a backend service
    alert("Thank you for subscribing to our newsletter!");
    const form = e.target as HTMLFormElement;
    form.reset();
  };

  const handleViewDetails = (product: FeaturedProduct) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // Calculate cart total
  const cartTotal = cart.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );

  useEffect(() => {
    if (location.state && location.state.productId) {
      const product = featuredProducts.find(p => p.id === location.state.productId);
      if (product) {
        handleViewDetails(product);
      }
    }
  }, [location.state, featuredProducts, handleViewDetails]);

  return (
    <div className="home-page">
      {/* Cart Button */}
      <div className="cart-button" onClick={() => setCartOpen(!cartOpen)}>
        <span className="cart-icon">🛒</span>
        <span className="cart-count">{cart.length}</span>
      </div>

      {/* Cart Sidebar */}
      <div className={`cart-sidebar${cartOpen ? ' open' : ''}`}>
        <div className="cart-header">
          <h3>Your Cart</h3>
          <button className="close-cart" onClick={() => setCartOpen(false)}>
            ×
          </button>
        </div>
        {cart.length > 0 ? (
          <>
            <div className="cart-items">
              {cart.map((item) => (
                <div key={item.product.id} className="cart-item">
                  <img
                    src={item.product.imageUrl}
                    alt={item.product.name}
                    className="cart-item-image"
                  />
                  <div className="cart-item-details">
                    <h4>{item.product.name}</h4>
                    <p className="cart-item-price">
                      ${item.product.price.toFixed(2)}
                    </p>
                    <div className="quantity-control">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    className="remove-item"
                    onClick={() => removeFromCart(item.product.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="cart-footer">
              <div className="cart-total">
                <span>Total:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <button className="checkout-btn" onClick={() => navigate('/checkout', { state: { cart } })}>
                Proceed to Checkout
              </button>
            </div>
          </>
        ) : (
          <div className="empty-cart">
            <p>Your cart is empty.</p>
            <p>Add some products to get started!</p>
          </div>
        )}
      </div>

      {/* Enhanced Hero Section with CTA buttons */}
      <section className="hero">
        <div className="hero-content">
          <h1>Welcome to Sip'N'Security</h1>
          <p className="tagline">Where bourbon meets cybersecurity insights</p>
          <p className="subtitle">Your one‑stop shop for premium podcast merchandise and expert cybersecurity knowledge</p>
          <div className="hero-cta">
            <Link to="/store" className="btn btn-primary">Shop Now</Link>
            <Link to="/podcast" className="btn btn-secondary">Listen to Podcast</Link>
          </div>
        </div>
      </section>

      {/* Enhanced About Our Hosts Section with images and animations */}
      <section id="about" className={`about ${isVisible.about ? 'visible' : ''}`}>
        <div className="about-content">
          <div className="about-text">
            <h2>About Our Hosts</h2>
            
            {/* Justin's Section */}
            <div className="host-section">
              <div className="host-details">
                <div className="host-image-home">
                  <img src={justinImage} alt="Justin Fields, host of Sip'N'Security" onError={(e) => { e.currentTarget.src = justinImageFallback }} />
                </div>
                <div className="host-text">
                  <h3>Justin Fields</h3>
                  <p className="about-description">
                    Born in Maryland but raised in Colorado, Justin leads Cross Architectural sales in Channels at Cisco. 
                    With over 25 years in Information Technology, he's known for simplifying the complexity of technology 
                    to make it consumable for executives. When he's not at work, you'll find him smoking meat on his Traeger, 
                    enjoying bourbon or four-wheeling through the mountains of Colorado.
                  </p>
                </div>
              </div>
            </div>

            {/* Dave's Section */}
            <div className="host-section">
              <div className="host-details">
                <div className="host-image-home">
                  <img src={daveImage} alt="Dave Abbott, host of Sip'N'Security" onError={(e) => { e.currentTarget.src = daveImageFallback }} />
                </div>
                <div className="host-text">
                  <h3>Dave Abbott</h3>
                  <p className="about-description">
                    Born and raised in Philly but now soaking up the North Carolina sunshine, Dave Abbott is a 
                    Cybersecurity Engineering Manager in Channels at Cisco. Equal parts tech geek and teacher, 
                    Dave is passionate about cracking the code at the intersection of networking and security. 
                    When he's off the clock, you'll find him out on the water, perfecting his BBQ game, 
                    sampling a good bourbon, or tackling his latest house project.
                  </p>
                </div>
              </div>
            </div>

            <p>
              Listen to their latest episodes on Spotify and YouTube while enjoying exclusive merchandise
              that celebrates the intersection of cybersecurity culture and premium spirits. Join thousands of professionals
              who trust Justin & Dave for both security advice and bourbon recommendations.
            </p>
            <Link to="/podcast" className="btn btn-text">Discover the Podcast →</Link>
          </div>
        </div>
      </section>

      {/* Enhanced Featured Products Section with badges and ratings */}
      <section id="featured-products" className={`featured-products ${isVisible.products ? 'visible' : ''}`}>
        <h2>Featured Merchandise</h2>
        <div className="carousel-container">
          <Carousel
            additionalTransfrom={0}
            arrows
            autoPlay
            autoPlaySpeed={2000}
            centerMode={false}
            className="featured-carousel"
            containerClass="carousel-container"
            dotListClass=""
            draggable
            focusOnSelect={false}
            infinite
            itemClass="carousel-item-padding-40-px"
            keyBoardControl
            minimumTouchDrag={80}
            pauseOnHover
            renderButtonGroupOutside={false}
            renderDotsOutside={false}
            responsive={{
              superLargeDesktop: { breakpoint: { max: 4000, min: 1376 }, items: 4 },
              desktop: { breakpoint: { max: 1376, min: 998 }, items: 3 },
              tablet: { breakpoint: { max: 998, min: 768 }, items: 2 },
              mobile: { breakpoint: { max: 768, min: 0 }, items: 1 }
            }}
            showDots={false}
            sliderClass=""
            slidesToSlide={1}
            swipeable
          >
            {featuredProducts.map((product) => (
              <div className="featured-product-card" key={product.id}>
                <div className="featured-badge">Featured</div>
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="featured-product-image"
                />
                <div className="featured-product-info">
                  <h3>{product.name}</h3>
                  <p className="featured-price">${formatPrice(product.price)}</p>
                  <div className="featured-actions">
                    <button
                      className="view-details-btn"
                      onClick={() => handleViewDetails(product)}
                    >
                      View Details
                    </button>
                    <button
                      className="add-to-cart-btn"
                      onClick={() => addToCart(product, 1)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
        <div className="view-all">
          <Link to="/store" className="btn btn-secondary">View All Products</Link>
        </div>
      </section>

      {isModalOpen && selectedProduct && (
        <div className="product-modal-overlay" onClick={handleCloseModal}>
          <div className="product-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={handleCloseModal}>×</button>
            <div className="modal-content">
              <div className="modal-image">
                <img src={selectedProduct.imageUrl} alt={selectedProduct.name} />
              </div>
              <div className="modal-info">
                <h2>{selectedProduct.name}</h2>
                <p className="modal-price">${formatPrice(selectedProduct.price)}</p>
                {selectedProduct.description && (
                  <div className="modal-description">{selectedProduct.description}</div>
                )}
                <ul className="modal-list">
                  {selectedProduct.category && (
                    <li><strong>Category:</strong> {selectedProduct.category}</li>
                  )}
                  {typeof selectedProduct.stock !== 'undefined' && (
                    <li><strong>Stock:</strong> {selectedProduct.stock}</li>
                  )}
                </ul>
                <button
                  className="modal-add-to-cart"
                  onClick={() => {
                    addToCart(selectedProduct, 1);
                    handleCloseModal();
                  }}
                >
                  Add to Cart 🛒
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;